/* eslint-disable @typescript-eslint/no-explicit-any */
// AuthGuard.tsx
import { Navigate, useLocation } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { Box } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { selectAuthSlice, logout as appLogout } from "../store/authSlice";
import { ROLE } from "../utils/constants";

export const ROLE_ACCESS: any = {
  [ROLE.ADMIN]: {
    // Admin can access everything
    patterns: [/^\//, /^\/dashboard\//],
    redirect: () => "/",
  },
  [ROLE.MANAGER]: {
    // Manager can access operations, finance and their own routes
    patterns: [
      /^\/$/,
      /^\/dashboard\/senior-manager/,
      /^\/dashboard\/operations/,
      /^\/dashboard\/finance/,
    ],
    redirect: () => `/`,
    // redirect: (companyId: string) => `/dashboard/senior-manager/home/${companyId}`
  },
  [ROLE.FINANCE]: {
    // Finance can only access finance routes
    patterns: [/^\/dashboard\/finance/],
    redirect: (companyId: string) => `/dashboard/finance/home/${companyId}`,
  },
  [ROLE.OPERATIONS]: {
    // Operations can only access operations routes
    patterns: [/^\/dashboard\/operations/],
    redirect: (companyId: string) => `/dashboard/operations/home/${companyId}`,
  },
  [ROLE.OTHER]: {
    patterns: [/^\/sbee/],
    redirect: () => "/sbee",
  },
};

const AuthGuard = ({ children }: { children: React.ReactNode }) => {
  const auth = useAppSelector(selectAuthSlice);
  const { logout } = useAuth0();
  const dispatch = useAppDispatch();
  const location = useLocation();

  // Check authentication
  // Check authentication
  if (!auth.isAuthenticated || !auth.user) {
    const intendedDestination =
      location.pathname + location.search + location.hash;

    localStorage.clear();
    dispatch(appLogout());

    // Only add returnTo if it's not the login page and not the root
    const shouldAddReturn =
      location.pathname !== "/auth/login" && location.pathname !== "/";

    let returnTo = `${window.location.origin}/auth/login`;
    if (shouldAddReturn) {
      returnTo += `?returnTo=${encodeURIComponent(intendedDestination)}`;
    }

    logout({
      logoutParams: { returnTo },
    });

    // Remove the Navigate component since logout handles the redirect
    return null;
  }

  const { company_id, access_level: role } = auth.user;

  // Routes accessible to all authenticated users
  const publicAuthenticatedRoutes = ["/dashboard/my-account"];

  if (publicAuthenticatedRoutes.includes(location.pathname)) {
    return <>{children}</>;
  }

  const userRoleAccess = ROLE_ACCESS[role];

  if (!userRoleAccess) {
    logout({
      logoutParams: { returnTo: `${window.location.origin}/auth/login` },
    });
    dispatch(appLogout());
    return <Box>User does not have the required role to access the app</Box>;
  }

  // Check if current path matches any of the allowed patterns for user's role
  const hasAccess = userRoleAccess.patterns.some((pattern: any) =>
    pattern.test(location.pathname)
  );

  // If user doesn't have access to this route, redirect to their appropriate page
  if (!hasAccess) {
    return <Navigate to={userRoleAccess.redirect(company_id)} replace />;
  }

  return <>{children}</>;
};

export default AuthGuard;
